import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAdmin } from "./AdminLayout";
import { useAdminLang, STATUS_LABELS } from "./adminLang";
import { ChevronDown, ChevronUp, FileDown } from "lucide-react";

const TITLES = {
  fr: { coverage: "Vérifications de couverture", quotes: "Demandes de devis", contact: "Messages de contact", applications: "Candidatures", newsletter: "Abonnés newsletter" },
  en: { coverage: "Coverage checks", quotes: "Quote requests", contact: "Contact messages", applications: "Applications", newsletter: "Newsletter subscribers" },
};

const rowSummary = (kind, r) => {
  if (kind === "newsletter") return [r.email, r.locale];
  const p = r.payload || r;
  return [p.full_name || p.name || "—", p.email || "—", p.service || p.department || p.position || ""];
};

const Inbox = () => {
  const { kind } = useParams();
  const { canLeads } = useAdmin();
  const { lang, tr } = useAdminLang();
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get(`/admin/requests/${kind}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    setRows([]); setOpenId(null); load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/requests/${kind}/${id}`, { status });
    load();
  };

  const downloadCv = async (id) => {
    try {
      const res = await api.get(`/admin/applications/${id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "cv"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* no CV */ }
  };

  const payloadOf = (r) => r.payload || { name: r.name, email: r.email, phone: r.phone, position: r.position, location: r.location, linkedin: r.linkedin, cover_note: r.cover_note };

  return (
    <div data-testid={`admin-inbox-${kind}`}>
      <h1 className="text-2xl font-black text-white">{TITLES[lang][kind] || kind}</h1>
      <div className="mt-6 bg-[#101214] border border-white/10 rounded-2xl overflow-hidden">
        {rows.length === 0 && <p className="px-6 py-10 text-center text-white/35 text-sm">{tr("Aucune donnée.", "No data.")}</p>}
        {rows.map((r) => (
          <div key={r.id} className="border-b border-white/5 last:border-0">
            <button onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`inbox-row-${r.id}`}
              className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{rowSummary(kind, r)[0]}</p>
                <p className="text-xs text-white/45 truncate">{rowSummary(kind, r)[1]} · {rowSummary(kind, r)[2]}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {r.status && <span className="text-[11px] font-bold bg-[#FF6600]/15 text-[#FF9900] rounded-full px-2.5 py-1">{STATUS_LABELS[lang][r.status] || r.status}</span>}
                <span className="text-[11px] text-white/35">{new Date(r.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR")}</span>
                {openId === r.id ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
              </div>
            </button>
            {openId === r.id && (
              <div className="px-5 pb-5 pt-1">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 bg-white/[0.03] rounded-xl p-4">
                  {Object.entries(payloadOf(r)).filter(([k, v]) => v && !["consent", "locale"].includes(k)).map(([k, v]) => (
                    <div key={k} className="text-xs">
                      <dt className="text-white/40 font-bold">{k}</dt>
                      <dd className="text-white/80 break-words">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-3 flex items-center gap-3">
                  {canLeads && r.status && (
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`inbox-status-${r.id}`}
                      className="bg-white/5 border border-white/15 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-[#FF6600]">
                      {Object.entries(STATUS_LABELS[lang]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  )}
                  {kind === "applications" && r.cv_path && (
                    <button onClick={() => downloadCv(r.id)} data-testid={`inbox-cv-${r.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF9900] border border-[#FF6600]/40 rounded-full px-3.5 py-2 hover:bg-[#FF6600]/10">
                      <FileDown size={13} /> {tr("Télécharger le CV", "Download CV")}
                    </button>
                  )}
                  {kind === "quotes" && r.attachment_path && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.attachment_path}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF9900] border border-[#FF6600]/40 rounded-full px-3.5 py-2 hover:bg-[#FF6600]/10">
                      <FileDown size={13} /> {tr("Pièce jointe", "Attachment")}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Inbox;
